import { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { insightsService, DashboardRow } from '../../services/insights.service';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { adAccountsService, AdAccount } from '../../services/ad-accounts.service';
import StatusBadge from '../../components/StatusBadge';
import AdStatusButtons from '../../components/AdStatusButtons';
import AdStatusToggle from '../../components/AdStatusToggle';
import { useAdStatus, useMayChangeStatus, NOUN } from '../../components/ad-status';
import DateRangePicker from '../../components/DateRangePicker';
import CreativePreviewDialog from '../../components/CreativePreviewDialog';
import { syncService } from '../../services/sync.service';
import { creativeGenerationService } from '../../services/creative-generation.service';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAdsManagerState, LEVELS, AdsLevel } from './useAdsManagerState';

/** Mirrors the server's cap so the limit is visible before the request, not after it. */
const MAX_SOURCES = 10;

const LEVEL_LABEL: Record<AdsLevel, string> = { campaign: 'Campaign', adset: 'Ad Set', ad: 'Ad' };

const formatCurrency = (v: number) => `$${v.toFixed(2)}`;
const formatPct = (v: number) => `${v.toFixed(2)}%`;

// Colours and weights per tracking-page.cells.tsx, zero and null included.
function colorCell(v: number | null, fmt: (n: number) => string) {
  if (v == null) return <Box component="span" sx={{ color: 'text.disabled' }}>-</Box>;
  return (
    <Box component="span" sx={{
      color: v > 0 ? 'success.main' : v < 0 ? 'error.main' : 'text.secondary',
      fontWeight: v !== 0 ? 600 : 400,
    }}>
      {fmt(v)}
    </Box>
  );
}

export default function AdsManagerPage() {
  const {
    dateRange, setDateRange,
    adAccountId, setAdAccountId, level, setLevel,
    campaignIds, adsetIds, includeArchived, setIncludeArchived,
    drillInto, clearSelection, filters,
  } = useAdsManagerState();

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const EMPTY: GridRowSelectionModel = { type: 'include', ids: new Set<string>() };
  const [selection, setSelection] = useState<GridRowSelectionModel>(EMPTY);
  const { data: accounts = [] } = useQuery({
    queryKey: ['ad-accounts'],
    queryFn: () => adAccountsService.query({ limit: 100 }).then((r) => r.data),
  });

  const [previewAdId, setPreviewAdId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [sort, setSort] = useState<GridSortModel>([{ field: 'spend', sort: 'desc' }]);

  const { data, isLoading } = useQuery({
    queryKey: ['ads-manager', filters, pagination, sort],
    queryFn: () => insightsService.getDashboard({
      ...filters,
      limit: pagination.pageSize,
      offset: pagination.page * pagination.pageSize,
      order_by: sort[0]?.field,
      order: sort[0]?.sort === 'asc' ? 'asc' : 'desc',
    }),
    placeholderData: keepPreviousData,
  });
  const rows = data?.rows ?? [];
  const totals = data?.totals;

  // A rowCount of 0 mid-load makes MUI reset the page, stranding everything past page 2.
  const rowCountRef = useRef(0);
  if (totals?.total_row_count != null) rowCountRef.current = totals.total_row_count;
  const rowCount = rowCountRef.current;

  // Pinned from the server's totals, so it covers the filtered set rather than the page.
  const totalsRow = useMemo<DashboardRow | null>(() => {
    if (!totals || totals.total_row_count === 0) return null;
    const { spend, revenue, impressions, clicks, conversions } = totals;
    return {
      entity_id: '__totals__',
      name: `Total (${totals.total_row_count})`,
      status: '',
      effective_status: null,
      status_stale_since: null,
      daily_budget: null,
      creative_image_url: null,
      creative_video_url: null,
      headline: null,
      spend, revenue,
      profit: revenue - spend,
      roi: spend > 0 ? ((revenue - spend) / spend) * 100 : 0,
      roi_yesterday: null,
      roi_2d_ago: null,
      impressions, clicks, conversions,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      cpc: clicks > 0 ? spend / clicks : 0,
      cpa: conversions > 0 ? spend / conversions : 0,
      roas: spend > 0 ? revenue / spend : 0,
    };
  }, [totals]);

  // At least the last 3 days, so recent attribution refreshes whatever window is shown.
  const syncMutation = useMutation({
    mutationFn: () => {
      const day = (n: number) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
      return syncService.syncInsights(adAccountId, {
        date_from: filters.date_from < day(3) ? filters.date_from : day(3),
        date_to: filters.date_to > day(0) ? filters.date_to : day(0),
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Insights sync started', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['ads-manager'] });
    },
    onError: () => enqueueSnackbar('Failed to start insights sync', { variant: 'error' }),
  });

  // Ads carry a creative; campaigns and ad sets do not, so this only offers at ad level.
  const generateMutation = useMutation({
    mutationFn: () => creativeGenerationService.createRunFromAds(selectedIds),
    onSuccess: (run) => {
      enqueueSnackbar(`${run.prompts_added} variations proposed`, { variant: 'success' });
      navigate(`/creative-generation/${run.id}`);
    },
    onError: () => enqueueSnackbar('Could not propose variations', { variant: 'error' }),
  });

  const mayChangeStatus = useMayChangeStatus();
  const { ask, dialogs } = useAdStatus(level, 'ads-manager');

  const scoped = campaignIds.length > 0 || adsetIds.length > 0;

  const columns = useMemo<GridColDef<DashboardRow>[]>(() => {
    const num = (c: Partial<GridColDef<DashboardRow>> & { field: string }): GridColDef<DashboardRow> => ({
      type: 'number', align: 'right', headerAlign: 'right', width: 110, ...c,
    } as GridColDef<DashboardRow>);

    return [
      // Ad level only: a parent's arbitrary ad image would read as "this is the creative".
      ...(level === 'ad' ? [{
        field: 'creative_image_url',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        renderCell: (p: { value?: string | null; row: DashboardRow }) => (p.value ? (
          <Tooltip title={p.row.headline || p.row.name}>
            <img
              src={p.value}
              alt=""
              onClick={(e) => { e.stopPropagation(); setPreviewAdId(p.row.entity_id); }}
              style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
            />
          </Tooltip>
        ) : <Box sx={{ width: 36, height: 36, bgcolor: 'action.hover', borderRadius: 1 }} />),
      } as GridColDef<DashboardRow>] : []),
      { field: 'name', headerName: LEVEL_LABEL[level], flex: 1, minWidth: 220 },
      {
        field: 'status',
        headerName: 'Status',
        width: 175,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusBadge status={p.value} effectiveStatus={p.row.effective_status}
              staleSince={p.row.status_stale_since} />
            {mayChangeStatus && (
              <AdStatusToggle
                status={p.value}
                noun={NOUN[level][0]}
                onToggle={(next) => ask([p.row.entity_id], next)}
              />
            )}
          </Box>
        ),
      },
      ...(level !== 'ad' ? [num({
        field: 'daily_budget',
        headerName: 'Daily Budget',
        valueFormatter: (v: number | null) => (v != null ? formatCurrency(v) : '-'),
      })] : []),
      num({ field: 'spend', headerName: 'Spend', valueFormatter: (v: number) => formatCurrency(v) }),
      num({ field: 'revenue', headerName: 'Revenue', valueFormatter: (v: number) => formatCurrency(v) }),
      num({ field: 'profit', headerName: 'Profit', renderCell: (p) => colorCell(p.value, formatCurrency) }),
      num({ field: 'roi', headerName: 'ROI %', width: 90, renderCell: (p) => colorCell(p.value, formatPct) }),
      num({ field: 'roi_yesterday', headerName: 'ROI Yest.', width: 100, renderCell: (p) => colorCell(p.value, formatPct) }),
      num({ field: 'roi_2d_ago', headerName: 'ROI 2d Ago', width: 105, renderCell: (p) => colorCell(p.value, formatPct) }),
      num({ field: 'impressions', headerName: 'Impressions', valueFormatter: (v: number) => v.toLocaleString() }),
      num({ field: 'clicks', headerName: 'Clicks', width: 90, valueFormatter: (v: number) => v.toLocaleString() }),
      num({ field: 'conversions', headerName: 'Conv.', width: 90 }),
    ];
  }, [level, mayChangeStatus, ask]);

  // 'exclude' inverts .ids: read alone it drills into exactly the rows not picked.
  const selectedIds = useMemo(() => {
    const ids = new Set(Array.from(selection.ids).map(String));
    return selection.type === 'exclude'
      ? rows.map((r) => r.entity_id).filter((id) => !ids.has(id))
      : Array.from(ids);
  }, [selection, rows]);

  const onTab = (next: AdsLevel) => {
    drillInto(next, selectedIds);
    setSelection(EMPTY);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Ads Manager</Typography>
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Ad account</InputLabel>
          <Select label="Ad account" value={adAccountId} onChange={(e) => setAdAccountId(e.target.value)}>
            <MenuItem value="">All accounts</MenuItem>
            {accounts.map((a: AdAccount) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant={includeArchived ? 'contained' : 'outlined'}
          onClick={() => setIncludeArchived(!includeArchived)}
        >
          Archived
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={!adAccountId || syncMutation.isPending}
          onClick={() => syncMutation.mutate()}
        >
          Sync now
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Tabs value={level} onChange={(_, v) => setLevel(v)}>
          {LEVELS.map((l) => <Tab key={l.key} value={l.key} label={l.label} />)}
        </Tabs>
        {scoped && (
          <Chip
            size="small"
            color="primary"
            label={campaignIds.length
              ? `${campaignIds.length} campaign${campaignIds.length > 1 ? 's' : ''} selected`
              : `${adsetIds.length} ad set${adsetIds.length > 1 ? 's' : ''} selected`}
            onDelete={clearSelection}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {selectedIds.length > 0 && (
          <AdStatusButtons
            entityType={level}
            ids={selectedIds}
            invalidateKey="ads-manager"
            alreadyInState={(status) => selectedIds.filter(
              (id) => rows.find((r) => r.entity_id === id)?.status === status).length}
            onDone={() => setSelection(EMPTY)}
          />
        )}
        {selectedIds.length > 0 && level === 'ad' && (
          <Tooltip title={selectedIds.length > MAX_SOURCES
            ? `Pick at most ${MAX_SOURCES} ads — each one costs a call to propose variations`
            : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                disabled={generateMutation.isPending || selectedIds.length > MAX_SOURCES}
                onClick={() => generateMutation.mutate()}
                data-testid="generate-variations"
              >
                Generate variations from {selectedIds.length}
              </Button>
            </span>
          </Tooltip>
        )}
        {selectedIds.length > 0 && level !== 'ad' && (
          <Button size="small" variant="contained" onClick={() => onTab(level === 'campaign' ? 'adset' : 'ad')}>
            View {selectedIds.length} selected {level === 'campaign' ? 'ad sets' : 'ads'}
          </Button>
        )}
      </Stack>

      {previewAdId && (
        <CreativePreviewDialog open onClose={() => setPreviewAdId(null)} adId={previewAdId} />
      )}

      {dialogs}

      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(r: DashboardRow) => r.entity_id}
        loading={isLoading}
        checkboxSelection
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        onRowDoubleClick={(p: { id: string | number }, e: React.MouseEvent) => {
          if (level === 'ad' || p.id === '__totals__') return;
          // Double-clicking a word to copy it, or the checkbox, is not a request to drill.
          if ((e.target as HTMLElement).closest('.MuiDataGrid-cellCheckbox')) return;
          if (!window.getSelection()?.isCollapsed) return;
          drillInto(level === 'campaign' ? 'adset' : 'ad', [String(p.id)]);
          setSelection(EMPTY);
          setPagination((prev) => ({ ...prev, page: 0 }));
        }}
        density="compact"
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={sort}
        onSortModelChange={setSort}
        pagination
        pinnedRows={totalsRow ? { top: [totalsRow] } : undefined}
        sx={{ height: 'calc(100vh - 260px)', '& .MuiDataGrid-pinnedRows': { fontWeight: 600 } }}
      />
    </Box>
  );
}
